import { NGROK } from "../APIs";
import { apiClient } from "../utils/apiClient";

// Base prefix for all Vault APIs
const VAULT_BASE = `${NGROK}/api/vault`;

// Normalize various error shapes into a readable string
function getErrorMessage(err: any): string {
    if (!err) return "Unknown error";
    if (typeof err === "string") return err;
    if (typeof err.message === "string") return err.message;
    if (typeof err.error === "string") return err.error;
    if (typeof err.detail === "string") return err.detail;
    try {
        return JSON.stringify(err);
    } catch {
        return "Unknown API Error";
    }
}

async function request<T>(method: string, url: string, data?: any, params?: Record<string, any>): Promise<T> {
    try {
        return await apiClient<T>({ method: method as any, url, data, params });
    } catch (e) {
        throw getErrorMessage(e);
    }
}

// ===================== 1) Authentication =====================
export interface IssueTokenRequest {
    clientName: string;
    appName: string;
    client_secret: string;
    grantType: "client_credentials";
}

export interface IssueTokenResponse {
    accessToken: string;
    tokenType: string;
    expiresIn: number;
    issuedAt: string;
    expiresAt: string;
    scopes: string[];
}

export async function createVaultToken(body: IssueTokenRequest): Promise<IssueTokenResponse> {
    const url = `${VAULT_BASE}/auth/token`;
    return request<IssueTokenResponse>("post", url, body);
}

export async function revokeVaultToken(): Promise<void> {
    const url = `${VAULT_BASE}/auth/token`;
    await request<void>("delete", url);
}

// ===================== 2) Secret Management (Token-protected) =====================
export interface SecretMetadata {
    label: string;
    clientName: string;
    vaultPath: string;
    version: number;
    accessCount: number;
    lastUpdated: string;
    description?: string;
}

export interface SecretReadResponse extends SecretMetadata {
    secretData?: Record<string, any>;
}

export interface SecretUpsertRequest {
    clientName: string;
    label: string;
    description?: string;
    data: Record<string, any>;
}

export async function readVaultSecret(clientName: string, label: string): Promise<SecretReadResponse> {
    const url = `${VAULT_BASE}/admin/secrets`;
    return request<SecretReadResponse>("get", url, undefined, { clientName, label });
}

export async function updateVaultSecret(clientName: string, label: string, body: SecretUpsertRequest): Promise<SecretMetadata> {
    const url = `${VAULT_BASE}/admin/secrets`;
    return request<SecretMetadata>("put", url, body, { clientName, label });
}

export async function deleteVaultSecret(clientName: string, label: string): Promise<void> {
    const url = `${VAULT_BASE}/admin/secrets`;
    await request<void>("delete", url, undefined, { clientName, label });
}

export async function listVaultSecretsForClient(clientName: string): Promise<SecretMetadata[]> {
    const url = `${VAULT_BASE}/admin/secrets`;
    return request<SecretMetadata[]>("get", url, undefined, { clientName, list: true });
}

export async function getVaultSecretValue(clientName: string, vaultPath: string): Promise<Record<string, any>> {
    const url = `${VAULT_BASE}/admin/secrets/value`;
    return request<Record<string, any>>("get", url, undefined, { clientName, secretLabel: vaultPath });
}

// Legacy raw-path read
export interface LegacySecretResponse {
    path: string;
    data: Record<string, any>;
}

export async function readVaultSecretByPath(path: string): Promise<LegacySecretResponse> {
    const url = `${VAULT_BASE}/admin/secrets/${path}`; // path may contain slashes
    return request<LegacySecretResponse>("get", url);
}

// ===================== 3) Admin – Secrets =====================
export interface AdminCreateSecretRequest {
    clientName: string;
    label: string;
    description?: string;
    data: Record<string, any>;
}

export async function createVaultSecret(body: AdminCreateSecretRequest): Promise<SecretMetadata> {
    const url = `${VAULT_BASE}/admin/secrets`;
    return request<SecretMetadata>("post", url, body);
}

export interface AdminListSecretsResponse {
    items: SecretMetadata[];
    count: number;
}

// Convenience: return { secrets } to match current UI expectations
export async function listVaultSecrets(): Promise<{ secrets: SecretMetadata[]; count: number }> {
    const url = `${VAULT_BASE}/admin/secrets`;
    const res = await request<AdminListSecretsResponse>("get", url);
    return { secrets: res.items, count: res.count };
}

// ===================== 4) Admin – Clients & Applications =====================
export interface VaultClientCreateRequest {
    name: string;
    description?: string;
}

export interface VaultClientResponse {
    client_name: string;
    client_id: string;
    description?: string;
    enabled: boolean;
    created_at: string;
    updated_at?: string;
    message?: string;
    apps_count: number;
    secrets_count: number;
}

export interface VaultClientsListResponse {
    clients: VaultClientResponse[];
    count: number;
}

export async function createVaultClient(body: VaultClientCreateRequest): Promise<VaultClientResponse> {
    const url = `${VAULT_BASE}/admin/clients`;
    return request<VaultClientResponse>("post", url, body);
}

export async function listVaultClients(): Promise<VaultClientsListResponse> {
    const url = `${VAULT_BASE}/admin/clients`;
    return request<VaultClientsListResponse>("get", url);
}

export async function getVaultClient(clientName: string): Promise<VaultClientResponse> {
    const url = `${VAULT_BASE}/admin/clients`;
    return request<VaultClientResponse>("get", url, undefined, { clientName });
}

export async function updateVaultClient(clientName: string, body: { name?: string; description?: string }): Promise<VaultClientResponse> {
    const url = `${VAULT_BASE}/admin/clients`;
    return request<VaultClientResponse>("put", url, body, { clientName });
}

export async function deleteVaultClient(clientName: string): Promise<{ message?: string }> {
    const url = `${VAULT_BASE}/admin/clients`;
    return request<{ message?: string }>("delete", url, undefined, { clientName });
}

export async function disableVaultClient(clientName: string): Promise<{ message?: string }> {
    const url = `${VAULT_BASE}/admin/clients/disable`;
    return request<{ message?: string }>("post", url, undefined, { clientName });
}

export async function enableVaultClient(clientName: string): Promise<{ message?: string }> {
    const url = `${VAULT_BASE}/admin/clients/enable`;
    return request<{ message?: string }>("post", url, undefined, { clientName });
}

// Applications
export interface VaultAppCreateRequest {
    appName: string;
    description?: string;
    scopes: string[]; // e.g. ["secret:read", ...]
    allowedLabels: string[];
}

export interface VaultAppResponse {
    app_name: string;
    client_name: string;
    description?: string;
    scopes: string[];
    allowed_labels: string[];
    enabled: boolean;
    created_at: string;
    updated_at?: string;
    app_secret?: string;
    warning?: string;
    app_id?: string;
}

export interface VaultAppsListResponseAll {
    applications: VaultAppResponse[];
    count: number;
}

export async function createVaultApp(clientName: string, body: VaultAppCreateRequest): Promise<VaultAppResponse> {
    const url = `${VAULT_BASE}/admin/clients/apps`;
    return request<VaultAppResponse>("post", url, body, { clientName });
}

export async function listVaultAppsForClient(clientName: string): Promise<{ applications: VaultAppResponse[]; count?: number }> {
    const url = `${VAULT_BASE}/admin/clients/apps`;
    const apps = await request<VaultAppResponse[]>("get", url, undefined, { clientName });
    return { applications: apps, count: apps.length };
}

export async function listVaultApps(): Promise<VaultAppsListResponseAll> {
    const url = `${VAULT_BASE}/admin/clients/apps`;
    return request<VaultAppsListResponseAll>("get", url);
}

export async function getVaultApp(appName: string): Promise<VaultAppResponse> {
    const url = `${VAULT_BASE}/admin/apps`;
    return request<VaultAppResponse>("get", url, undefined, { appName });
}

export async function updateVaultAppScopes(appName: string, body: { name: string, allowedLabels: string[], scopes: string[] }): Promise<{ message?: string }> {
    const url = `${VAULT_BASE}/admin/apps/updateclientApps`;
    return request<{ message?: string }>("put", url, body, { appName });
}

export async function regenerateVaultAppSecret(appName: string): Promise<VaultAppResponse> {
    const url = `${VAULT_BASE}/admin/apps/regenerate-secret`;
    return request<VaultAppResponse>("post", url, undefined, { appName });
}

export async function disableVaultApp(appName: string): Promise<{ message?: string }> {
    const url = `${VAULT_BASE}/admin/apps/disable`;
    return request<{ message?: string }>("post", url, undefined, { appName });
}

export async function enableVaultApp(appName: string): Promise<{ message?: string }> {
    const url = `${VAULT_BASE}/admin/apps/enable`;
    return request<{ message?: string }>("post", url, undefined, { appName });
}

export async function deleteVaultApp(appName: string): Promise<{ message?: string }> {
    const url = `${VAULT_BASE}/admin/apps`;
    return request<{ message?: string }>("delete", url, undefined, { appName });
}

export interface AuditLogItem {
    id: number | string;
    timestamp: string;
    action: string;
    clientId?: string;
    clientName: string;
    appName?: string | null;
    resourcePath?: string | null;
    success: boolean;
    errorMessage?: string | null;
    ipAddress?: string;
    userAgent?: string;
    requestId?: string;
    metadata?: string;
}

export interface AuditLogsResponse {
    items: AuditLogItem[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
}

export async function listVaultAuditLogs(params?: { page?: number; size?: number }): Promise<AuditLogsResponse> {
    const url = `${VAULT_BASE}/admin/audit-logs`;
    return request<AuditLogsResponse>("get", url, undefined, { page: params?.page ?? 0, size: params?.size ?? 50 });
}

export interface OverviewResponse {
    counts: Record<string, number>;
    "Recent Activity": {
        data: Array<{ action: string; appName: string; resourcePath: string; time: string }>;
        count: number;
    };
    "Most Accessed Secrets": {
        data: Array<{ secretPath: string; clientName: string; appName: string; count: number }>;
        count: number;
    };
}

export async function getVaultOverview(): Promise<OverviewResponse> {
    const url = `${VAULT_BASE}/admin/overview`;
    return request<OverviewResponse>("get", url);
}

export async function legacyWriteSecret(secretName: string, data: Record<string, string>): Promise<string> {
    const url = `${VAULT_BASE}/write/${encodeURIComponent(secretName)}`;
    return request<string>("post", url, data);
}

export async function legacyReadSecret(secretName: string, key: string): Promise<string> {
    const url = `${VAULT_BASE}/read/${encodeURIComponent(secretName)}/${encodeURIComponent(key)}`;
    return request<string>("get", url);
}

export interface VaultTokenResponse {
    id: number;
    app: string;
    client: string;
    token: string;
    tokenHash: string;
    sourceIp: string;
    status: string;
    created: string;
    lastUsed: string | null;
    expiresAt: string;
    scopes: string;
    description: string | null;
    isRevoked: boolean;
    isExpired: boolean;
    clientId?: string;
}

export interface VaultTokensListResponse {
    tokens: VaultTokenResponse[];
    pagination: {
        page: number;
        size: number;
        total: number;
        totalPages: number;
    };
    message: string;
}

export interface VaultTokenDetailsResponse {
    id: number;
    app: string;
    client: string;
    tokenHash: string;
    token: string;
    status: string;
    created: string;
    expiresAt: string;
    lastUsed: string | null;
    sourceIp: string;
    scopes: string[];
    description: string | null;
    isRevoked: boolean;
    isExpired: boolean;
    clientId?: string;
}

export async function listVaultTokens(params?: {
    page?: number;
    size?: number;
    status?: 'active' | 'revoked' | 'expired';
}): Promise<VaultTokensListResponse> {
    const url = `${VAULT_BASE}/admin/tokens`;
    return request<VaultTokensListResponse>("get", url, undefined, {
        page: params?.page ?? 0,
        size: params?.size ?? 50,
        ...(params?.status && { status: params.status })
    });
}

export async function getVaultTokenDetails(id: number): Promise<VaultTokenDetailsResponse> {
    const url = `${VAULT_BASE}/admin/tokens/${id}`;
    return request<VaultTokenDetailsResponse>("get", url);
}

export async function revokeVaultTokenById(id: number): Promise<{ message: string; tokenId: number; status: string }> {
    const url = `${VAULT_BASE}/admin/tokens/${id}/revoke`;
    return request<{ message: string; tokenId: number; status: string }>("post", url);
}

export async function revokeAppAllTokens(clientName: string, appName: string): Promise<{ success: boolean; message: string; tokensRevoked: number; clientName: string; appName: string }> {
    const url = `${VAULT_BASE}/admin/tokens/revoke-app-tokens`;
    return request<{ success: boolean; message: string; tokensRevoked: number; clientName: string; appName: string }>("post", url, undefined, { clientName, appName });
}
